import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCheckboxChangeEvent } from '../../events/blocks-checkbox-change';
import type { SelectValue } from '../../util/common-modal';
import { isString } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../skeleton/skeleton';
import type { ValidationStatus } from './checkbox-field.model';
import styles from './checkbox-field.styles';

/**
 * @slot default - The component default content
 * @slot field-note - The component field-note content
 * @slot error - The component error content
 * @slot warning - warningNote slot
 *
 * @fires bl-checkbox-change - emitted when checkbox is toggled.
 *
 * @csspart base  - The component's base wrapper.
 * @csspart container  - The component's container wrapper.
 * @csspart input  - The component's input element.
 * @csspart custom-wrapper- The component's custom wrapper .
 * @csspart custom-check - The component's custom check element.
 * @csspart part-label - The component's label element.
 * @csspart part-default - The component's default slot.
 * @csspart part-notation- The component's notation element.
 * @csspart loader  - The component's skeleton loader.
 * @csspart field-note-loader - The component's skeleton field note loader element.
 * @csspart field-note-skeleton - The component's skeleton field note skeleton element.
 * @csspart field-note-wrapper - The component's filed-note wrapper.
 * @csspart part-field-note - The component's filed-note slot.
 * @csspart part-warning  - The component's warning slot.
 * @csspart part-error - The component's error slot.
 * @csspart error-icon  - The component's error icon.
 * @csspart warning-icon  - The component's warning icon.
 * @csspart highlight- The component's highlight field.
 * @csspart highlight-wrapper - The component's highlight field wrapper.
 */
export class BlocksCheckboxField extends BlocksElement {
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * Status of checkbox
   */
  @property({ type: String })
  status: ValidationStatus;

  /**
   * Searched String
   */
  @property({ type: String })
  searchText: string | undefined;

  /**
   * Adds a hover effect for the checkbox item
   */
  @property({ type: Boolean })
  hasHover: boolean | undefined;

  /**
   * disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * value attribute
   */
  @property()
  value: SelectValue | undefined;

  /**
   * Id
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * Name attribute
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * Indeterminate property
   */
  @property({ type: Boolean })
  indeterminate: boolean | undefined;

  /**
   * Checked property
   */
  @property({ type: Boolean, reflect: true })
  checked: boolean | undefined;

  /**
   *  Error message for the field
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   *  Warning message for the field
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Description or Error message for the field
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required: boolean | undefined;

  /**
   * aria-describedby attribute property
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * The field's label property
   */
  @property({ type: String })
  label: string | undefined;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * isInline property
   * When true, this property overrides the default flex behavior, aligning the element inline instead of filling the container width.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean })
  readonly: boolean | undefined;

  /**
   * Role attribute property
   */
  @property({ type: String })
  roleForCheckboxField: string | undefined;

  /**
   * Name or Title for the warning icon
   * accessibility purposes, specifically for screen reader announcements in browse mode
   */
  @property({ type: String })
  warningIconTitle = 'warning icon';

  /**
   * Name or Title for the error icon
   * accessibility purposes, specifically for screen reader announcements in browse mode
   */
  @property({ type: String })
  errorIconTitle = 'error icon';

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav element
   */
  @property({ type: String })
  arialabel: string | undefined;

  private internals_ = this.attachInternals?.();

  public type = 'checkbox';

  /**
   * id for label to be aria-labelledby for the input
   */
  private labelId = nanoid();

  get inputValue() {
    return isString(this.value) ? this.value : (this.value?.value ?? '');
  }
  @query('input') private inputEl!: HTMLInputElement;

  /**
   * Indicates that the checkbox should have input focus when the page loads.
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  /**
   * To check whether the role is option
   */
  private get isRoleOption() {
    return this.roleForCheckboxField === 'option';
  }

  protected slots = new SlotController(this, null, 'error', 'warning', 'field-note');

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }
  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }
  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid();
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('checked') || _changedProperties.has('value')) {
      this.internals_?.setFormValue(this.checked ? this.inputValue : null);
    }
    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
  }

  override focus(): void {
    this.inputEl?.focus();
    this.inputEl?.classList.add('cat-c-checkbox-focused');
  }
  /**
   * @ignore
   *  To remove focus class when the element loses focus
   */
  removeFocusClass = () => {
    this.inputEl?.classList.remove('cat-c-checkbox-focused');
  };

  /** To check for search string and highlight accordingly  */
  private checkSubstring(label: string) {
    if (this.searchText && !this.disabled && label) {
      const searchText = this.searchText.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$&');

      const parts = label.split(new RegExp(`(${searchText})`, 'gi'));
      return html`<span part="highlight-wrapper"
        >${parts.map((part) =>
          part.toLowerCase() === this.searchText?.toLowerCase().trim()
            ? html`<mark part="highlight" class="cat-c-checkbox-field__highlighted">${part}</mark>`
            : html`${part}`
        )}</span
      >`;
    } else {
      return label;
    }
  }

  /**
   * Retrieve label from value string/value (SelectValue type) incase label prop is not provided
   * @param value - Type SelectValue
   * @returns
   */
  private getCheckboxLabel(value: SelectValue | undefined) {
    return isString(value) ? value : value?.label || '';
  }

  /**To render the checkbox items which contains searched string*/
  private renderItem() {
    const label = this.label || this.getCheckboxLabel(this.value);
    return this.checkSubstring(label?.trim());
  }

  /**
   * 1) To prevent toggling of checkbox in browse/virtual mode of screen reader
   * 2) To toggle on interactive mode and dispatch the event
   */
  private handleOnChange(): void {
    if (this.readonly || this.disabled) {
      this.inputEl!.checked = !!this.checked; /* 1 */
      return;
    }
    this.checked = !this.checked; /* 2 */
    if (this.indeterminate === true) {
      this.indeterminate = false;
    }
    this.dispatch({
      eventName: 'bl-checkbox-change',
      detailObj: {
        checked: this.checked,
        indeterminate: this.indeterminate,
        value: this.value
      }
    }) as BlocksCheckboxChangeEvent;
  }

  /**
   * Handle on keydown events
   * 1) If the Enter key is pressed, then check the checkbox
   * 2) If user presses up arrow key,then previousSibling will focussed. If current focussed item is first item,
   * then after pressing up arrow last item will be focussed.
   * 3) 2) If user presses down arrow key,then nextsibling will focussed. If current focussed item is last item,
   * then after pressing down arrow first item will be focussed.
   * 4) To prevent click in readonly state with keyboard event.
   */
  private handleKeydown(e: KeyboardEvent) {
    if (!this.disabled) {
      if (!this.readonly && e.key === KEYS.ENTER) {
        /* 1 */
        this.handleOnChange();
      }
      /* 4 */
      if (this.readonly && e.key === KEYS.SPACE) {
        e.preventDefault();
        return;
      }
    } else {
      if (e.key === KEYS.SPACE) {
        e.preventDefault();
      }
    }
  }

  /**
   * Display error note from slot or from errorNote property
   */
  private getErrorNote() {
    return html`
      <cat-icon-warning-diamond-filled
        part="error-icon"
        exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
        color="error"
        .iconTitle=${this.errorIconTitle}
        class="cat-c-checkbox-field__icon"
      ></cat-icon-warning-diamond-filled>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-checkbox--fieldnote cat-c-checkbox-errornote-slot"
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.errorNote || this.hasErrorSlot ? this.ariaDescribedBy : undefined)}
        .status=${'error'}
      >
        <slot part="part-error" name="error">${this.errorNote}</slot>
      </cat-field-note>
    `;
  }

  /**
   * Display warning note from slot or from warningNote property
   */
  private getWarningNote() {
    return html`
      <cat-icon-warning-triangle-filled
        part="warning-icon"
        exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
        color="knockout-brand"
        .iconTitle=${this.warningIconTitle}
        class="cat-c-checkbox-field__icon"
      ></cat-icon-warning-triangle-filled>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-checkbox--fieldnote cat-c-checkbox-warningnote-slot"
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.warningNote || this.hasWarningSlot ? this.ariaDescribedBy : undefined)}
        .status=${'warning'}
      >
        <slot part="part-warning" name="warning">${this.warningNote}</slot>
      </cat-field-note>
    `;
  }

  /**
   * 1) Display field note from slot or from fieldNote property
   */
  private getFieldNote() {
    /* 1 */
    return html`
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-checkbox--fieldnote cat-c-checkbox-fieldnote-slot"
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
      >
        <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
      </cat-field-note>
    `;
  }

  private hasFieldNote() {
    return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  /**
   * To render loading variant
   */
  private renderLoading() {
    const componentClassNames = this.componentClassNames('cat-c-checkbox-field', {
      'cat-c-checkbox-field-is-loading': this.isLoading
    });
    return html` <div part="base" class="${componentClassNames}">
      <div part="container" class="cat-c-checkbox-field__container">
        <div part="input" class="cat-c-checkbox__input"></div>
        <span part="custom-check" class="cat-c-checkbox__custom-check"></span>
        <cat-skeleton
          part="loader"
          exportparts=${this.getExportParts('skeleton')}
          class="cat-c-checkbox-loading"
          size="sm"
          width="100px"
        ></cat-skeleton>
      </div>
      ${this.hasFieldNote()
        ? html`
            <div part="field-note-loader" class="cat-c-checkbox-field__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                arialabel=""
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                size="sm"
                width="100px"
              ></cat-skeleton>
            </div>
          `
        : nothing}
    </div>`;
  }

  /**
   * Generates the HTML for the input label of the checkbox field.
   * @returns {TemplateResult | typeof nothing} The HTML template for the input label if `hideLabel` is false, otherwise `nothing`.
   */
  private inputLabel() {
    return this.hideLabel
      ? nothing
      : html`<label
          aria-hidden="true"
          id=${ifDefined(this.labelId)}
          part="part-label"
          class="cat-c-checkbox-field__label"
          for="${ifDefined(this.fieldId)}"
          ><slot part="part-default" class="cat-c-checkbox-field__label_wrapper">${this.renderItem()}</slot
          ><span part="part-notation" class="cat-c-checkbox__notation">${this.notation}</span>
        </label>`;
  }

  render(): TemplateResult<1> {
    const statusArray = ['error', 'warning'];
    const componentClassNames = this.componentClassNames('cat-c-checkbox-field', {
      'cat-is-indeterminate': this.indeterminate === true,
      'cat-c-checkbox-field--hidden-label': this.hideLabel,
      'cat-has-hover': this.hasHover === true,
      'cat-is-checked': this.checked === true,
      'cat-c-checkbox-field-is-error': this.status === 'error',
      'cat-c-checkbox-field-is-readonly': this.readonly === true,
      'cat-is-inline': this.isInline === true,
      'cat-is-disabled': this.disabled === true,
      'cat-c-checkbox-field--has-field-note': this.hasFieldNoteSlot || (!statusArray.includes(this.status ?? '') && this.fieldNote),
      'cat-c-checkbox-field--has-warning': this.hasWarningSlot || (this.status === 'warning' && this.warningNote),
      'cat-c-checkbox-field--has-error': this.hasErrorSlot || (this.status === 'error' && this.errorNote)
    });

    this.setAriaDescribedBy();
    if (this.isLoading) {
      return this.renderLoading();
    }

    return html`
      <div part="base" role=${ifDefined(!this.isRoleOption ? this.roleForCheckboxField : undefined)} class="${componentClassNames}">
        <div part="container" class="cat-c-checkbox-field__container">
          <input
            part="input"
            id="${ifDefined(this.fieldId)}"
            role=${ifDefined(this.isRoleOption ? this.roleForCheckboxField : undefined)}
            type="checkbox"
            name="${ifDefined(this.name)}"
            class="cat-c-checkbox__input"
            .value="${this.inputValue}"
            .checked="${this.checked ?? false}"
            .indeterminate="${this.indeterminate ?? false}"
            aria-disabled="${ifDefined(this.disabled)}"
            aria-selected=${ifDefined(this.isRoleOption ? (this.checked ? true : false) : undefined)}
            ?disabled=${!this.hasHover ? this.disabled : null}
            aria-readonly="${ifDefined(this.readonly)}"
            ?autofocus=${this.autoFocus}
            @change=${this.handleOnChange}
            @keydown=${this.handleKeydown}
            @blur=${this.removeFocusClass}
            aria-describedby="${ifDefined(this.ariaDescribedBy)}"
            aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
            aria-labelledby="${this.hideLabel ? nothing : ifDefined(this.labelId)}"
            aria-label="${ifDefined(this.arialabel)}"
          />
          <span part="custom-wrapper" class="cat-c-checkbox__custom_wrapper"></span>
          <span part="custom-check" class="cat-c-checkbox__custom-check"></span>
          ${this.inputLabel()}
        </div>
        <div class="cat-c-checkbox-field__fieldnote-container">
          <!--- field note -->
          ${this.getFieldNote()}
          <!--- error note -->
          ${this.getErrorNote()}
          <!--- warning note -->
          ${this.getWarningNote()}
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-checkbox-field') === undefined) {
  customElements.define('cat-checkbox-field', BlocksCheckboxField);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-checkbox-field': BlocksCheckboxField;
  }
}
